import React, { useState, useEffect, useCallback, useRef } from "react";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import toast from "react-hot-toast";

import { InputField } from "../InputField";
import { ComboboxField } from "../ComboboxField";
import { TextAreaField } from "../TextAreaField";

import {
  addQuestionnaire,
  updateQuestionnaire,
  deleteQuestionnaire,
  getFormsByType,
  getQuestionnaireDetail,
  combineSections,
} from "../../../utils/formStorage";

const fieldTypeOptions = [
  { id: "text", name: "Text" },
  { id: "number", name: "Number" },
  { id: "select", name: "Select" },
  { id: "radio", name: "Radio" },
  { id: "checkbox", name: "Checkbox" },
  { id: "textarea", name: "Textarea" },
];

// Stable option list for the Criteria dropdown. Declared at module level so the
// array and each option object keep the same identity across renders —
// ComboboxField runs an effect keyed on the props it receives, so an inline
// array rebuilt each render keeps re-firing that effect.
const criteriaOptions = [
  { id: "Weighted", name: "Weighted" },
  { id: "Unweighted", name: "Unweighted" },
];

// Preview component for rendering questions (similar to RenderInputField in CreateRFQTabs)
const PreviewQuestion = ({ questionData, onRemove, index }) => {
  const { componentId, mandatory, question, answerDefinition, serialNo } = questionData;

  const renderField = () => {
    switch (answerDefinition?.type) {
      case "textarea":
        return (
          <Col xs={11} md={11} lg={11} className="commLeftRightPadding">
            <TextAreaField
              label={serialNo + ". " + question?.label}
              id={componentId}
              name={componentId}
              className="inputBox"
              placeholder=""
              required={mandatory}
              readOnly
            />
          </Col>
        );
      case "select":
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding">
            <ComboboxField
              label={serialNo + ". " + question?.label}
              placeholder=""
              data={answerDefinition?.options}
              id={componentId}
              iconClassName="dropdownIcon"
              name={componentId}
              code={componentId}
              description="name"
              required={mandatory}
              disabled
            />
          </Col>
        );
      case "radio":
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding">
            <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion w-100">
              {serialNo + ". " + question?.label} {mandatory && "*"}
            </label>
            {answerDefinition?.options?.map((option, optIndex) => (
              <div key={optIndex} className="block">
                <input
                  type="radio"
                  className="form-check-input fs-5 ml-3"
                  id={`${componentId}-${optIndex}`}
                  name={componentId}
                  value={option?.value ?? option?.name ?? ""}
                  disabled
                />
                <span className="ml-3">{option?.value} </span>
              </div>
            ))}
          </Col>
        );
      case "checkbox":
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding">
            <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion w-100">
              {serialNo + ". " + question?.label} {mandatory && "*"}
            </label>
            {answerDefinition?.options?.map((option, optIndex) => (
              <div key={optIndex} className="block">
                <input
                  type="checkbox"
                  className="form-check-input fs-5 ml-3"
                  id={`${componentId}-${optIndex}`}
                  name={componentId}
                  value={option?.value ?? option?.name ?? ""}
                  disabled
                />
                <span className="ml-3">{option?.value} </span>
              </div>
            ))}
          </Col>
        );
      case "text":
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding block">
            <InputField
              label={serialNo + ". " + question?.label}
              type="text"
              className="inputBox"
              id={componentId}
              name={componentId}
              placeholder=""
              required={mandatory}
              readOnly
            />
          </Col>
        );
      case "number":
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding block">
            <InputField
              label={serialNo + ". " + question?.label}
              type="number"
              className="inputBox"
              id={componentId}
              name={componentId}
              placeholder=""
              required={mandatory}
              readOnly
            />
          </Col>
        );
      default:
        return (
          <Col xs={11} md={11} lg={11} className="commTopButtonRightLeftPadding">
            <span>{serialNo + ". " + question?.label} (Unknown type)</span>
          </Col>
        );
    }
  };

  return (
    <div className="formBuildSection mb-3">
      <Row>
        {renderField()}
        <Col xs={1} md={1} lg={1} className="commTopButtonRightLeftPadding mt-4">
          <i
            onClick={() => onRemove(index)}
            className="bi bi-trash close-btn text-danger"
            style={{ cursor: "pointer", fontSize: "1.2rem" }}
          ></i>
        </Col>
      </Row>
    </div>
  );
};

const GenerateForm = (props) => {
  const { formType, onSave, onCancel } = props;

  // State for saved forms
  const [savedForms, setSavedForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [isNewForm, setIsNewForm] = useState(false);
  const [formName, setFormName] = useState("");

  // State for delete modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFormsList, setDeleteFormsList] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  // State for sections
  const [sections, setSections] = useState([]);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);

  // State for current field being built
  const [currentField, setCurrentField] = useState({
    label: "",
    type: "text",
    mandatory: false,
    options: [{ value: "", isDefault: false }],
    criteria: "Weighted",
    weightage: 0,
    weightageStatus: false,
    scoreStatus: false,
  });

  // Refs to avoid dependency issues
  const savedFormsRef = useRef(savedForms);
  const selectedFormIdRef = useRef(selectedFormId);

  useEffect(() => {
    savedFormsRef.current = savedForms;
  }, [savedForms]);

  useEffect(() => {
    selectedFormIdRef.current = selectedFormId;
  }, [selectedFormId]);

  // Load saved forms from API on mount and when formType changes
  useEffect(() => {
    const fetchForms = async () => {
      try {
        const forms = await getFormsByType(formType);
        setSavedForms(forms);
      } catch (error) {
        console.error("Failed to load forms:", error);
        toast.error("Failed to load saved forms", { duration: 3000, position: "top-right" });
      }
    };
    fetchForms();
  }, [formType]);

  // Filter forms by type (already filtered by API, but keep for safety)
  const filteredForms = savedForms.filter(f => f.formType === formType);

  // Handle form selection from dropdown
  const handleFormSelect = useCallback(async (formId) => {
    console.log(">>formid",formId)
    // Prevent processing the same formId again (avoid infinite loop)
    if (formId === selectedFormIdRef.current) {
      return;
    }

    if (!formId) {
      setSelectedFormId("");
      setIsNewForm(false);
      setFormName("");
      setSections([{ id: `sec_${Date.now()}`, name: "", questions: [] }]);
      setActiveSectionIndex(0);
      setCurrentField({ label: "", type: "text", mandatory: false, options: [{ value: "", isDefault: false }] });
      return;
    }

    const form = savedFormsRef.current.find(f => f.id === formId);
    if (form) {
      setSelectedFormId(formId);
      setIsNewForm(false);
      setFormName(form.name);

      // Fetch full form detail including sections - use code field (e.g., QUESTN0001)
      const formCode = form.code || formId;
      try {
        const detail = await getQuestionnaireDetail(formCode);
        if (detail && detail.sections) {
          setSections(detail.sections.map(s => ({ ...s, questions: s.questions || [] })));
        } else {
          // Fallback if no sections in detail
          setSections([{ id: `sec_${Date.now()}`, name: "", questions: [] }]);
        }
      } catch (error) {
        console.error("Failed to load form detail:", error);
        toast.error("Failed to load form details", { duration: 3000, position: "top-right" });
        setSections([{ id: `sec_${Date.now()}`, name: "", questions: [] }]);
      }

      setActiveSectionIndex(0);
      setCurrentField({ label: "", type: "text", mandatory: false, options: [{ value: "", isDefault: false }] });
    }
  }, []);

  // Stable wrapper for ComboboxField getvalue to avoid inline function creating infinite loop
  const handleFormSelectWrapper = useCallback((sel) => {
    handleFormSelect(sel?.id || "");
    console.log(">>sel",sel)
    
  }, [handleFormSelect]);

  // Handle new form button
  const handleNewForm = useCallback(() => {
    setIsNewForm(true);
    setSelectedFormId("");
    setFormName("");
    setSections([{ id: `sec_${Date.now()}`, name: "", questions: [] }]);
    setActiveSectionIndex(0);
    setCurrentField({ label: "", type: "text", mandatory: false, options: [{ value: "", isDefault: false }] });
  }, []);

  // Open delete modal - fetches both COMM and TECH forms
  const handleOpenDeleteModal = useCallback(async () => {
    setDeleteLoading(true);
    try {
      const [commForms, techForms] = await Promise.all([
        getFormsByType("COMM"),
        getFormsByType("TECH")
      ]);
      // Add formType to each form for display
      const allForms = [
        ...commForms?.map(f => ({ ...f, formType: "COMM" })),
        ...techForms?.map(f => ({ ...f, formType: "TECH" }))
      ];
      setDeleteFormsList(allForms);
      setShowDeleteModal(true);
    } catch (error) {
      console.error("Failed to load forms for deletion:", error);
      toast.error("Failed to load forms", { duration: 3000, position: "top-right" });
    } finally {
      setDeleteLoading(false);
    }
  }, []);

  // Handle form selection in delete modal
  const handleSelectFormToDelete = useCallback((form) => {
    setFormToDelete(form);
  }, []);

  // Confirm and execute deletion
  const handleConfirmDelete = useCallback(async () => {
    if (!formToDelete) {
      toast.error("No form selected to delete", { duration: 3000, position: "top-right" });
      return;
    }

    setDeleteLoading(true);
    try {
      // Use documentNumber (code) for deletion, not MongoDB _id
      await deleteQuestionnaire(formToDelete.code || formToDelete.id);

      // Refresh the current form type list
      const forms = await getFormsByType(formType);
      setSavedForms(forms);

      // Close modal and reset state
      setShowDeleteModal(false);
      setFormToDelete(null);
      setDeleteFormsList([]);

      // If the deleted form was currently selected, reset to new form
      if (selectedFormId === formToDelete.id) {
        setSelectedFormId("");
        setIsNewForm(true);
        setFormName("");
        setSections([{ id: `sec_${Date.now()}`, name: "", questions: [] }]);
        setActiveSectionIndex(0);
        setCurrentField({ label: "", type: "text", mandatory: false, options: [{ value: "", isDefault: false }] });
      }

      toast.success("Form deleted successfully!", { duration: 3000, position: "top-right" });
    } catch (error) {
      console.error("Failed to delete form:", error);
      toast.error("Failed to delete form", { duration: 3000, position: "top-right" });
    } finally {
      setDeleteLoading(false);
    }
  }, [formToDelete, formType, selectedFormId]);

  // Close delete modal
  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setFormToDelete(null);
  }, []);

  // Add a new section (max 5)
  const addSection = useCallback(() => {
    if (sections?.length >= 5) {
      toast.error("Maximum 5 sections allowed", { duration: 3000, position: "top-right" });
      return;
    }
    const newSection = { id: `sec_${Date.now()}`, name: `Section ${sections?.length + 1}`, questions: [] };
    setSections(prev => [...prev, newSection]);
    setActiveSectionIndex(sections?.length);
  }, [sections?.length]);

  // Remove a section
  const removeSection = useCallback((index) => {
    if (sections?.length <= 1) {
      toast.error("At least one section is required", { duration: 3000, position: "top-right" });
      return;
    }
    setSections(prev => prev.filter((_, i) => i !== index));
    setActiveSectionIndex(prev => Math.min(prev, sections?.length - 2));
  }, [sections?.length]);

  // Update section name
  const updateSectionName = useCallback((index, name) => {
    setSections(prev => prev?.map((s, i) => i === index ? { ...s, name } : s));
  }, []);

  // Set active section
  const setActiveSection = useCallback((index) => {
    setActiveSectionIndex(index);
  }, []);

  // Handle field input changes (for currentField state)
  const handleFieldChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    setCurrentField(prev => {
      const updated = { ...prev, [name]: newValue };

      // Handle criteria change logic
      if (name === "criteria") {
        if (newValue === "Unweighted") {
          updated.weightage = 0;
          updated.weightageStatus = true;
          updated.scoreStatus = true;
        } else if (newValue === "Weighted") {
          updated.weightageStatus = false;
          updated.scoreStatus = false;
        }
      }

      // Handle weightage input - only allow numbers
      if (name === "weightage") {
        const numValue = parseInt(value) || 0;
        updated.weightage = numValue;
      }

      return updated;
    });
  }, []);

  // Handle form name input changes (for formName state)
  const handleFormNameChange = useCallback((e) => {
    const { value } = e.target;
    setFormName(value);
  }, []);

  // Stable wrapper for field type change (ComboboxField getvalue)
  const handleTypeChange = useCallback((sel) => {
    handleFieldChange({ target: { name: "type", value: sel?.id || "text" } });
  }, [handleFieldChange]);

  // Stable wrapper for the Criteria dropdown's getvalue. ComboboxField calls its
  // getvalue from an effect keyed on that prop (and on mount), so passing an
  // inline arrow — a NEW reference every render — re-fires the effect on every
  // render, which calls setCurrentField each time → "Maximum update depth
  // exceeded". The functional guard also makes the mount call a no-op when the
  // value hasn't actually changed.
  const handleCriteriaSelect = useCallback((sel) => {
    const nextCriteria = sel?.id || "Weighted";
    setCurrentField(prev => {
      if (prev.criteria === nextCriteria) return prev;
      const isUnweighted = nextCriteria === "Unweighted";
      return {
        ...prev,
        criteria: nextCriteria,
        weightage: isUnweighted ? 0 : prev.weightage,
        weightageStatus: isUnweighted,
        scoreStatus: isUnweighted,
      };
    });
  }, []);

  // Handle option changes for select/radio/checkbox
  const handleOptionChange = useCallback((index, value) => {
    setCurrentField(prev => {
      const updatedOptions = [...prev.options];
      updatedOptions[index] = {
        ...updatedOptions[index],
        id: index + 1,
        name: value,
        value: value,
      };
      return { ...prev, options: updatedOptions };
    });
  }, []);

  const handleDefaultChange = useCallback((index) => {
    setCurrentField(prev => ({
      ...prev,
      options: prev.options?.map((option, i) => ({
        ...option,
        isDefault: i === index,
      })),
    }));
  }, []);

  const handleAddOption = useCallback(() => {
    setCurrentField(prev => ({
      ...prev,
      options: [...prev.options, { value: "", isDefault: false }],
    }));
  }, []);

  const handleRemoveOption = useCallback((index) => {
    setCurrentField(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  }, []);

  // Generate field - add current field to active section
  const handleGenerateField = useCallback(() => {
    if (!currentField.label.trim()) {
      toast.error("Please enter a label", { duration: 3000, position: "top-right" });
      return;
    }

    const { label, type, mandatory, options, criteria, weightage, weightageStatus, scoreStatus } = currentField;

    // Validate options for select/radio/checkbox
    if (["select", "radio", "checkbox"].includes(type)) {
      const validOptions = options.filter(o => o.value && o.value.trim());
      if (validOptions?.length === 0) {
        toast.error("Please add at least one option", { duration: 3000, position: "top-right" });
        return;
      }
    }

    // Get next serial number across all sections
    let maxSerial = 0;
    sections.forEach(s => {
      s.questions.forEach(q => {
        if (q.serialNo > maxSerial) maxSerial = q.serialNo;
      });
    });

    const newQuestion = {
      componentId: `question_${maxSerial + 1}`,
      mandatory,
      question: { label: label.trim() },
      answerDefinition: {
        type,
        options: ["select", "radio", "checkbox"].includes(type)
          ? options.filter(o => o.value && o.value.trim())?.map((o, i) => ({
              ...o,
              id: i + 1,
              name: o.name || o.value,
            }))
          : [],
      },
      serialNo: maxSerial + 1,
      criteria: criteria || "Weighted",
      weightage: weightage || 0,
      weightageStatus: weightageStatus || false,
      scoreStatus: scoreStatus || false,
    };

    setSections(prev => {
      const updated = [...prev];
      updated[activeSectionIndex] = {
        ...updated[activeSectionIndex],
        questions: [...(updated[activeSectionIndex].questions || []), newQuestion],
      };
      return updated;
    });

    // Reset current field
    setCurrentField({ label: "", type: "text", mandatory: false, options: [{ value: "", isDefault: false }], criteria: "Weighted", weightage: 0, weightageStatus: false, scoreStatus: false });
  }, [currentField, sections, activeSectionIndex]);

  // Remove question from section
  const handleRemoveQuestion = useCallback((sectionIndex, questionIndex) => {
    setSections(prev => {
      const updated = [...prev];
      updated[sectionIndex] = {
        ...updated[sectionIndex],
        questions: updated[sectionIndex].questions.filter((_, i) => i !== questionIndex),
      };
      return updated;
    });
  }, []);

  // Save form - combine all sections and save via API
  const handleSaveForm = useCallback(async () => {
    if (!formName.trim()) {
      toast.error("Please enter a form name", { duration: 3000, position: "top-right" });
      return;
    }

    // Validate at least one section has questions
    const hasQuestions = sections.some(s => s.questions && s.questions?.length > 0);
    if (!hasQuestions) {
      toast.error("Please add at least one question", { duration: 3000, position: "top-right" });
      return;
    }

    // Combine all sections into flat array
    const combinedQuestions = combineSections(sections);

    // Create form object for API
    const formToSave = {
      name: formName.trim(),
      formType,
      sections: sections?.map(s => ({
        id: s.id,
        name: s.name,
        questions: s.questions || [],
      })),
    };

    try {
      if (selectedFormId) {
        // Update existing form
        await updateQuestionnaire(selectedFormId, formToSave);
      } else {
        // Add new form
        await addQuestionnaire(formToSave);
      }

      // Refresh the forms list
      const forms = await getFormsByType(formType);
      setSavedForms(forms);

      // Call onSave callback with combined questions for Redux
      if (onSave) {
        onSave(combinedQuestions);
      }

      // Close modal
      if (onCancel) {
        onCancel();
      }

      toast.success(selectedFormId ? "Form updated successfully!" : "Form saved successfully!", { duration: 3000, position: "top-right" });
    } catch (error) {
      console.error("Failed to save form:", error);
      toast.error("Failed to save form", { duration: 3000, position: "top-right" });
    }
  }, [formName, sections, selectedFormId, formType, onSave, onCancel]);

  // Current active section
  const activeSection = sections[activeSectionIndex];

  // Show options builder for select/radio/checkbox
  const showOptionsBuilder = ["select", "radio", "checkbox"].includes(currentField.type);

  return (
    <>
      <form>
        <Row className="pt-2 pb-4">
          {/* Left Panel - Builder */}
          <Col xs={12} md={6} lg={6} className="border-end">
            {/* Saved Forms Dropdown */}
            <Row className="mb-3">
              <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                <label className="form-label fw-bold">Select Saved Form</label>
                <ComboboxField
                  label=""
                  placeholder="Select a saved form..."
                  data={filteredForms?.map(f => ({ id: f.id, name: f.name, code: f.documentNumber || f._id }))}
                  id="savedFormSelect"
                  iconClassName="dropdownIcon"
                  name="savedFormSelect"
                  code="savedFormSelect"
                  description="name"
                  setValue={selectedFormId ? filteredForms?.find(f => f.id === selectedFormId) : ""}
                  getvalue={handleFormSelectWrapper}
                />
              </Col>
            </Row>

            {/* New Form Button & Delete Form Button */}
            <Row className="mb-3">
              <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
                <button
                  type="button"
                  onClick={handleNewForm}
                  className="btn btn-outline-primary w-full"
                  disabled={isNewForm && !selectedFormId}
                >
                  <i className="bi bi-plus-lg me-2"></i> New Form
                </button>
              </Col>
              <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
                <button
                  type="button"
                  onClick={handleOpenDeleteModal}
                  className="btn btn-outline-danger w-full"
                  disabled={deleteLoading}
                >
                  <i className="bi bi-trash me-2"></i> Delete Form
                </button>
              </Col>
            </Row>

            {/* Form Name Input (when new form) */}
            {(isNewForm || !selectedFormId) && (
              <Row className="mb-3">
                <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                  <InputField
                    type="text"
                    className="inputBox"
                    label="Form Name"
                    id="formName"
                    name="formName"
                    placeholder="Enter form name..."
                    value={formName}
                    onChange={handleFormNameChange}
                    required={true}
                  />
                </Col>
              </Row>
            )}

            <hr />

            {/* Sections List */}
            <Row className="mb-3">
              <Col xs={12} md={12} lg={12}>
                <h5>Sections (Max 5)</h5>
              </Col>
            </Row>

            {sections?.map((section, secIndex) => (
              <Row
                key={section.id}
                className={`mb-2 p-2 border rounded align-items-center ${
                  activeSectionIndex === secIndex ? "border-primary bg-white" : "bg-light"
                }`}
              >
                <Col xs={12} md={7} lg={7} className="commTopButtonRightLeftPadding">
                  <InputField
                    type="text"
                    className="inputBox"
                    label=""
                    placeholder="Section name..."
                    value={section.name}
                    onChange={(e) => updateSectionName(secIndex, e.target.value)}
                  />
                </Col>
                <Col xs={12} md={3} lg={3} className="commTopButtonRightLeftPadding mt-2 mt-md-0">
                  <button
                    type="button"
                    className={`btnTable btn-sm w-100 ${
                      activeSectionIndex === secIndex ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => setActiveSection(secIndex)}
                  >
                    {activeSectionIndex === secIndex ? (
                      <>
                        <i className="bi bi-pencil-square me-1"></i>Editing
                      </>
                    ) : (
                      <>
                        <i className="bi bi-pencil me-1"></i>Edit
                      </>
                    )}
                  </button>
                </Col>
                <Col xs={12} md={2} lg={2} className="commTopButtonRightLeftPadding mt-2 mt-md-0">
                  {sections?.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger w-100"
                      onClick={() => removeSection(secIndex)}
                      title="Remove section"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  )}
                </Col>
              </Row>
            ))}

            <Row className="mb-3">
              <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                <button
                  type="button"
                  onClick={addSection}
                  className="btn btn-outline-secondary w-full"
                  disabled={sections?.length >= 5}
                >
                  <i className="bi bi-plus-lg me-2"></i> Add Section ({sections?.length}/5)
                </button>
              </Col>
            </Row>

            <hr />

            {/* Field Builder for Active Section */}
            {activeSection && (
              <div>
                <Row className="mb-3">
                  <Col xs={12} md={12} lg={12}>
                    <h5>Build Field - Section: {activeSection.name || `Section ${activeSectionIndex + 1}`}</h5>
                  </Col>
                </Row>

                <Row>
                  <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                    <InputField
                      type="text"
                      className="inputBox"
                      label="Label"
                      id="label"
                      name="label"
                      placeholder="Enter question label..."
                      value={currentField.label}
                      onChange={handleFieldChange}
                      required={true}
                    />
                  </Col>

                  <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                    <ComboboxField
                      label="Type"
                      placeholder=""
                      data={fieldTypeOptions}
                      id="type"
                      iconClassName="dropdownIcon"
                      name="type"
                      code="type"
                      setValue={currentField.type}
                      getvalue={handleTypeChange}
                      required={true}
                    />
                  </Col>

                  <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                    <label htmlFor="mandatory" className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion">
                      Mandatory:
                    </label>
                    <input
                      type="checkbox"
                      className="form-check-input fs-5 ml-3"
                      name="mandatory"
                      id="mandatory"
                      checked={currentField.mandatory}
                      onChange={handleFieldChange}
                    />
                  </Col>
                </Row>

                {/* Evaluation Criteria Fields */}
                <Row>
                  <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
                    <ComboboxField
                      label="Criteria"
                      placeholder="Select Criteria"
                      data={criteriaOptions}
                      id="criteria"
                      iconClassName="dropdownIcon"
                      name="criteria"
                      code="id"
                      description="name"
                      setValue={
                        criteriaOptions.find(
                          (opt) => opt.id === currentField.criteria,
                        ) || null
                      }
                      getvalue={handleCriteriaSelect}
                      required={true}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={6} className="commTopButtonRightLeftPadding">
                    <InputField
                      type="number"
                      className="inputBox"
                      label="Weightage"
                      id="weightage"
                      name="weightage"
                      placeholder="Enter weightage..."
                      value={currentField.weightage || 0}
                      onChange={handleFieldChange}
                      disabled={currentField.weightageStatus}
                    />
                  </Col>
                </Row>

                {/* Options Builder for select/radio/checkbox */}
                {showOptionsBuilder && (
                  <Row>
                    <label className="block text-sm font-medium leading-6 text-gray-900 infoIconAccordion mt-4 col-12">
                      Options:
                    </label>

                    {currentField?.options?.map((option, optIndex) => (
                      <div key={optIndex}>
                        <Col xs={12} md={12} lg={12} className="commTopButtonRightLeftPadding">
                          <Row>
                            <Col xs={1} md={1} lg={1} className="commTopButtonRightLeftPadding">
                              <input
                                type="radio"
                                className="form-check-input fs-4 mr-3 mt-2"
                                name="defaultOption"
                                checked={option.isDefault}
                                onChange={() => handleDefaultChange(optIndex)}
                              />
                            </Col>
                            <Col xs={10} md={10} lg={10} className="commTopButtonRightLeftPadding block">
                              <InputField
                                type="text"
                                className="inputBox"
                                label=""
                                id="options"
                                name="options"
                                placeholder="Option value..."
                                value={option.value}
                                onChange={(e) => handleOptionChange(optIndex, e.target.value)}
                              />
                            </Col>
                            <Col xs={1} md={1} lg={1} className="commTopButtonRightLeftPadding mt-2">
                              <i
                                onClick={() => handleRemoveOption(optIndex)}
                                className="bi bi-trash close-btn text-danger"
                                style={{ cursor: "pointer", fontSize: "1.2rem" }}
                              ></i>
                            </Col>
                          </Row>
                        </Col>
                      </div>
                    ))}

                    <Col xs={12} md={3} lg={3} className="commLeftRightPadding">
                      <p className="addMore" onClick={handleAddOption} style={{ cursor: "pointer" }}>
                        <i className="bi bi-plus addMore"></i> Add Option
                      </p>
                    </Col>
                  </Row>
                )}

                <Row className="justify-end mt-3">
                  <Col xs={12} md={4} lg={4} className="commLeftRightPadding">
                    <button
                      type="button"
                      onClick={handleGenerateField}
                      className="btn btn-primary w-full justify-center rounded-md"
                    >
                      Generate Field
                    </button>
                  </Col>
                </Row>
              </div>
            )}
          </Col>

          {/* Right Panel - Preview */}
          <Col xs={12} md={6} lg={6}>
            <Row className="mb-3">
              <Col xs={12} md={10} lg={10}>
                <h3>Preview Questionnaire</h3>
              </Col>
              <Col xs={12} md={2} lg={2} className="text-end">
                <button
                  type="button"
                  onClick={handleSaveForm}
                  className="btn btn-success w-full justify-center rounded-md mb-2"
                >
                  Save Form
                </button>
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={12} lg={12}>
                {activeSection && activeSection.questions && activeSection.questions?.length > 0 ? (
                  activeSection?.questions?.map((question, qIndex) => (
                    <PreviewQuestion
                      key={question.componentId || qIndex}
                      questionData={question}
                      onRemove={(idx) => handleRemoveQuestion(activeSectionIndex, idx)}
                      index={qIndex}
                    />
                  ))
                ) : (
                  <div className="text-center text-muted mt-5">
                    <p>No fields added yet. Build fields on the left to see preview here.</p>
                  </div>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </form>

      {/* Delete Form Modal */}
      <Modal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Questionnaire Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading forms...</p>
            </div>
          ) : (
            <div>
              <p className="mb-3">Select a form to delete:</p>
              {deleteFormsList?.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <p>No forms found.</p>
                </div>
              ) : (
                <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <table className="table table-hover table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "40px" }}>Select</th>
                        <th>Form Name</th>
                        <th>Type</th>
                        <th>Document Number</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deleteFormsList?.map((form) => (
                        <tr
                          key={form.id}
                          onClick={() => handleSelectFormToDelete(form)}
                          style={{
                            cursor: "pointer",
                            backgroundColor: formToDelete?.id === form.id ? "#e7f3ff" : "transparent"
                          }}
                        >
                          <td className="text-center">
                            <input
                              type="radio"
                              name="deleteFormRadio"
                              checked={formToDelete?.id === form.id}
                              onChange={() => handleSelectFormToDelete(form)}
                            />
                          </td>
                          <td>{form.name}</td>
                          <td>
                            <span className="badge bg-primary">{form.formType}</span>
                          </td>
                          <td>{form.documentNumber || form.questionnaireNr || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {formToDelete && !deleteLoading && (
            <div className="w-100 d-flex justify-content-between">
              <div className="text-start text-danger small">
                Deleting: <strong>{formToDelete.name}</strong> ({formToDelete.formType})
              </div>
              <div>
                <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={deleteLoading}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleConfirmDelete} disabled={deleteLoading}>
                  {deleteLoading ? "Deleting..." : "Confirm Delete"}
                </Button>
              </div>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default GenerateForm;